/**
 * AnalyticsService.js - Service d'API pour les statistiques
 * Gère la communication avec le backend
 */

import axios from 'axios';
import { auth, waitForAuthState } from '../config/firebase';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

class AnalyticsService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000
    });

    // Ajouter un intercepteur pour le token Firebase (ID token de l'utilisateur connecté)
    this.client.interceptors.request.use(async (config) => {
      try {
        const user = auth.currentUser || await waitForAuthState();
        if (user) {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        throw e;
      }
      return config;
    });
  }

  // ===== TRACKING =====

  /**
   * Enregistrer une analyse
   */
  async trackAnalysis(data) {
    try {
      const response = await this.client.post('/track', {
        sessionId: data.sessionId,
        analysisType: data.analysisType,
        medications: data.medications,
        results: data.results,
        platform: data.platform,
        patientContext: data.patientContext,
        performance: data.performance,
        userId: data.userId || 'anonymous'
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Sauvegarder une analyse
   */
  async saveAnalysis(analysisId, data) {
    try {
      const response = await this.client.put(`/save/${analysisId}`, data);
      return response.data;
    } catch (error) {
      return { success: false };
    }
  }

  /**
   * Marquer une analyse comme consultée
   */
  async markAsConsidered(analysisId, data) {
    try {
      const response = await this.client.put(`/view/${analysisId}`, data);
      return response.data;
    } catch (error) {
      return { success: false };
    }
  }

  /**
   * Récupérer les analyses de l'utilisateur
   */
  async getUserAnalyses(params) {
    try {
      const response = await this.client.get('/analytics/user/analyses', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Supprimer une analyse
   */
  async deleteAnalysis(analysisId, reason) {
    try {
      const response = await this.client.delete(`/analytics/analyses/${analysisId}`, {
        data: { reason }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== STATISTIQUES ADMIN =====

  // Normaliser les dates pour couvrir toute la journée (UTC)
  // startDate "YYYY-MM-DD" -> "YYYY-MM-DDT00:00:00.000Z"
  // endDate   "YYYY-MM-DD" -> "YYYY-MM-DDT23:59:59.999Z"
  _normalizeDateRange(startDate, endDate) {
    const start = startDate && /^\d{4}-\d{2}-\d{2}$/.test(startDate)
      ? `${startDate}T00:00:00.000Z` : startDate;
    const end = endDate && /^\d{4}-\d{2}-\d{2}$/.test(endDate)
      ? `${endDate}T23:59:59.999Z` : endDate;
    return { start, end };
  }

  /**
   * Récupérer les statistiques globales
   */
  async getGlobalStatistics(startDate, endDate) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/global', {
        params: { startDate: start, endDate: end }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer les statistiques par utilisateur
   */
  async getStatisticsByUser(startDate, endDate, page = 1, limit = 10) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/by-user', {
        params: { startDate: start, endDate: end, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer les médicaments top
   */
  async getTopMedications(startDate, endDate, limit = 20) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/top-medications', {
        params: { startDate: start, endDate: end, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer les interactions fréquentes
   */
  async getFrequentInteractions(startDate, endDate, limit = 20, minOccurrences = 2) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/frequent-interactions', {
        params: { startDate: start, endDate: end, limit, minOccurrences }
      });
      return response.data;
    } catch (error) {
      return { success: false, data: [] };
    }
  }

  /**
   * Récupérer les tendances temporelles
   */
  async getTemporalTrends(startDate, endDate, granularity = 'daily') {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/trends', {
        params: { startDate: start, endDate: end, granularity }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Générer un rapport d'impact
   */
  async generateHealthImpactReport(startDate, endDate, aggregationLevel = 'GLOBAL', entityId) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.post('/admin/statistics/report', {
        startDate: start,
        endDate: end,
        aggregationLevel,
        entityId
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer les données du dashboard
   */
  async getDashboardData(startDate, endDate, period = 'daily') {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get('/admin/statistics/dashboard', {
        params: { startDate: start, endDate: end, period }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Exporter les statistiques
   */
  async exportStatistics(format, startDate, endDate) {
    try {
      const { start, end } = this._normalizeDateRange(startDate, endDate);
      const response = await this.client.get(`/admin/statistics/export/${format}`, {
        params: { startDate: start, endDate: end },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== GESTION DES RAPPORTS =====

  /**
   * Récupérer la liste des rapports d'impact
   */
  async getReports(page = 1, limit = 10, search = '', status = '') {
    try {
      const response = await this.client.get('/admin/statistics/reports', {
        params: { page, limit, search, status }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer un rapport par son ID
   */
  async getReportById(reportId) {
    try {
      const response = await this.client.get(`/admin/statistics/reports/${reportId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Supprimer un rapport
   */
  async deleteReport(reportId) {
    try {
      const response = await this.client.delete(`/admin/statistics/reports/${reportId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Gestion des erreurs
   */
  handleError(error) {
    if (error.response) {
      return {
        message: error.response.data?.error || 'Erreur serveur',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      return {
        message: 'Pas de réponse du serveur',
        status: 0
      };
    } else {
      return {
        message: error.message,
        status: -1
      };
    }
  }
}

export default new AnalyticsService();